import React, { useState, useEffect } from 'react';
import {
  FileText,
  TrendingUp,
  Award,
  AlertCircle,
  CheckCircle,
  XCircle,
  BarChart3,
  Brain,
  MessageCircle,
  Zap,
  RefreshCw,
  Loader,
  ThumbsUp,
  ThumbsDown,
  Star,
  ChevronDown,
  ChevronUp,
  Quote
} from 'lucide-react';
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  Legend
} from 'recharts';
import transcriptAnalysisService from './services/transcriptAnalysisService';

// Expandable Evidence Component
const ExpandableEvidence = ({ title, evidence, icon: Icon, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (!evidence || evidence.length === 0) {
    return null;
  }

  // Helper function to parse contextual evidence
  const parseEvidence = (evidenceText) => {
    // Try to extract context and quote from the formatted evidence
    // Format: "When asked about [context], candidate stated: '[quote]'"
    const contextMatch = evidenceText.match(/^(When asked about|In response to|During|When discussing|While discussing)\s+([^,]+),\s*candidate\s+(stated|explained|mentioned|described|said):\s*['"](.+)['"]$/i);

    if (contextMatch) {
      return {
        context: `${contextMatch[1]} ${contextMatch[2]}`,
        quote: contextMatch[4],
        hasStructure: true
      };
    }

    // If doesn't match the expected format, return as-is
    return {
      context: null,
      quote: evidenceText,
      hasStructure: false
    };
  };

  return (
    <div className="mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <div className="flex items-center">
          {Icon && <Icon className="w-4 h-4 mr-2 text-indigo-600" />}
          <span className="font-medium text-gray-700">{title}</span>
          <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
            {evidence.length} {evidence.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-3 pl-4 pr-2">
          {evidence.map((item, idx) => {
            const parsed = parseEvidence(item);

            return (
              <div key={idx} className="border-l-4 border-indigo-200 pl-4 py-2 bg-white rounded-r">
                {parsed.hasStructure ? (
                  <>
                    <p className="text-xs text-gray-500 italic mb-1">
                      {parsed.context}
                    </p>
                    <div className="flex items-start">
                      <Quote className="w-4 h-4 text-indigo-400 mr-2 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700 text-sm leading-relaxed">
                        "{parsed.quote}"
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {parsed.quote}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TranscriptAnalysis = ({ jobseeker, companyUser }) => {
  const [analysis, setAnalysis] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalysisData();
  }, [jobseeker.id]);

  const loadAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load status
      const statusResponse = await transcriptAnalysisService.getStatus(
        jobseeker.id,
        companyUser?.company
      );
      setStatus(statusResponse.data);

      // Load analysis if it exists
      if (statusResponse.data.hasAnalysis) {
        const analysisResponse = await transcriptAnalysisService.getAnalysis(
          jobseeker.id,
          companyUser?.company
        );
        setAnalysis(analysisResponse.data);
      }
    } catch (err) {
      console.error('Error loading analysis data:', err);
      setError(err.message || 'Failed to load transcript analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAnalysis = async () => {
    try {
      setGenerating(true);
      setError(null);

      const response = await transcriptAnalysisService.generateAnalysis(
        jobseeker.id,
        companyUser?.company
      );

      setAnalysis(response.data);
      setStatus({ ...status, hasAnalysis: true, isComplete: true });
    } catch (err) {
      console.error('Error generating analysis:', err);
      setError(err.message || 'Failed to generate transcript analysis');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateAnalysis = async () => {
    try {
      setGenerating(true);
      setError(null);

      const response = await transcriptAnalysisService.regenerateAnalysis(
        jobseeker.id,
        companyUser?.company
      );

      setAnalysis(response.data);
    } catch (err) {
      console.error('Error regenerating analysis:', err);
      setError(err.message || 'Failed to regenerate transcript analysis');
    } finally {
      setGenerating(false);
    }
  };

  const getRecommendationColor = (recommendation) => {
    const colors = {
      strong_yes: 'text-green-600 bg-green-50',
      yes: 'text-green-500 bg-green-50',
      maybe: 'text-yellow-600 bg-yellow-50',
      no: 'text-red-500 bg-red-50',
      strong_no: 'text-red-600 bg-red-50'
    };
    return colors[recommendation] || 'text-gray-600 bg-gray-50';
  };

  const getRecommendationIcon = (recommendation) => {
    if (recommendation === 'strong_yes' || recommendation === 'yes') {
      return <ThumbsUp className="w-6 h-6" />;
    } else if (recommendation === 'strong_no' || recommendation === 'no') {
      return <ThumbsDown className="w-6 h-6" />;
    }
    return <Star className="w-6 h-6" />;
  };

  const getRecommendationText = (recommendation) => {
    const texts = {
      strong_yes: 'Strong Yes',
      yes: 'Yes',
      maybe: 'Maybe',
      no: 'No',
      strong_no: 'Strong No'
    };
    return texts[recommendation] || recommendation;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981'; // green
    if (score >= 60) return '#3b82f6'; // blue
    if (score >= 40) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Loading analysis data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-red-800">Error</h3>
            <p className="text-red-700 mt-1">{error}</p>
            <button
              onClick={loadAnalysisData}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!status?.hasTranscript) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-yellow-800">No Transcript Available</h3>
            <p className="text-yellow-700 mt-1">
              No interview transcript found for this candidate. Please ensure an interview has been
              conducted and transcribed before generating analysis.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!status?.hasAnalysis) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-8">
        <div className="text-center">
          <FileText className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-indigo-800 mb-2">
            Transcript Analysis Not Generated
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Generate an AI-powered analysis of the interview transcript to evaluate the candidate's
            performance against job requirements.
          </p>
          <button
            onClick={handleGenerateAnalysis}
            disabled={generating}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
          >
            {generating ? (
              <>
                <Loader className="w-5 h-5 animate-spin mr-2" />
                Generating Analysis...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5 mr-2" />
                Generate Analysis
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  const assessment = analysis?.assessment || {};
  const jobRequirements = assessment.jobRequirements || {};

  // Prepare data for charts
  const overallScoreData = [
    {
      name: 'Score',
      value: assessment.overallScore || 0,
      fill: getScoreColor(assessment.overallScore || 0)
    }
  ];

  const criteriaData = [
    {
      name: 'Qualifications',
      score: jobRequirements.qualificationsMatch?.score || 0,
      fill: '#3b82f6'
    },
    {
      name: 'Skills',
      score: jobRequirements.skillsMatch?.score || 0,
      fill: '#10b981'
    },
    {
      name: 'Experience',
      score: jobRequirements.experienceMatch?.score || 0,
      fill: '#f59e0b'
    },
    {
      name: 'Communication',
      score: assessment.communicationSkills?.score || 0,
      fill: '#8b5cf6'
    },
    {
      name: 'Technical',
      score: assessment.technicalCompetency?.score || 0,
      fill: '#ec4899'
    }
  ];

  return (
    <div className="space-y-6 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold text-indigo-800 flex items-center">
            <Brain className="mr-3" />
            Transcript Analysis
          </h2>
          <p className="text-gray-600 mt-1">
            AI-powered evaluation of interview performance
          </p>
        </div>
        {/*
        <button
          onClick={handleRegenerateAnalysis}
          disabled={generating}
          className="px-4 py-2 bg-white border border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
          Regenerate
        </button>
        */}
      </div>

      {/* Overall Score and Recommendation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Overall Score */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-indigo-600" />
            Overall Score
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <RadialBarChart
              innerRadius="75%"
              outerRadius="95%"
              data={[
                { name: 'background', value: 100, fill: '#e5e7eb' },
                { name: 'score', value: assessment.overallScore || 0, fill: getScoreColor(assessment.overallScore || 0) }
              ]}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar
                background
                dataKey="value"
                cornerRadius={10}
                label={{
                  position: 'center',
                  content: ({ viewBox }) => {
                    const { cx, cy } = viewBox;
                    return (
                      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                        <tspan x={cx} y={cy - 5} fontSize="28" fontWeight="bold" fill="#1f2937">
                          {assessment.overallScore || 0}
                        </tspan>
                        <tspan x={cx} y={cy + 15} fontSize="12" fill="#6b7280">
                          out of 100
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>

        {/* Recommendation */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-indigo-600" />
            Hiring Recommendation
          </h3>
          <div className="flex flex-col items-center justify-center h-48">
            <div
              className={`p-4 rounded-full ${getRecommendationColor(assessment.recommendation)}`}
            >
              {getRecommendationIcon(assessment.recommendation)}
            </div>
            <p className="text-2xl font-bold mt-4 text-gray-800">
              {getRecommendationText(assessment.recommendation)}
            </p>
          </div>
        </div>
      </div>

      {/* Criteria Scores */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-indigo-600" />
          Detailed Scores
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={criteriaData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, 100]} />
            <Tooltip />
            <Bar dataKey="score" radius={[8, 8, 0, 0]}>
              {criteriaData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Job Requirements Analysis */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-indigo-600" />
          Job Requirements Match
        </h3>
        <div className="space-y-4">
          {/* Qualifications */}
          {jobRequirements.qualificationsMatch && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-gray-700">Qualifications Match</h4>
                <span className="text-indigo-600 font-semibold">
                  {jobRequirements.qualificationsMatch.score}/100
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-2">
                {jobRequirements.qualificationsMatch.analysis}
              </p>
              <ExpandableEvidence
                title="Evidence"
                evidence={jobRequirements.qualificationsMatch.evidence}
                icon={FileText}
              />
            </div>
          )}

          {/* Skills */}
          {jobRequirements.skillsMatch && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-gray-700">Skills Match</h4>
                <span className="text-indigo-600 font-semibold">
                  {jobRequirements.skillsMatch.score}/100
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-2">
                {jobRequirements.skillsMatch.analysis}
              </p>
              <ExpandableEvidence
                title="Evidence"
                evidence={jobRequirements.skillsMatch.evidence}
                icon={FileText}
              />
            </div>
          )}

          {/* Experience */}
          {jobRequirements.experienceMatch && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-gray-700">Experience Match</h4>
                <span className="text-indigo-600 font-semibold">
                  {jobRequirements.experienceMatch.score}/100
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-2">
                {jobRequirements.experienceMatch.analysis}
              </p>
              <ExpandableEvidence
                title="Evidence"
                evidence={jobRequirements.experienceMatch.evidence}
                icon={FileText}
              />
            </div>
          )}
        </div>
      </div>

      {/* Communication Skills */}
      {assessment.communicationSkills && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <MessageCircle className="w-5 h-5 mr-2 text-indigo-600" />
            Communication & Soft Skills
          </h3>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-gray-700">Overall Communication Score</h4>
              <span className="text-indigo-600 font-semibold">
                {assessment.communicationSkills.score}/100
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {assessment.communicationSkills.clarity !== undefined && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">Clarity</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {assessment.communicationSkills.clarity}
                  </p>
                </div>
              )}
              {assessment.communicationSkills.professionalism !== undefined && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">Professionalism</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {assessment.communicationSkills.professionalism}
                  </p>
                </div>
              )}
              {assessment.communicationSkills.confidence !== undefined && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">Confidence</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {assessment.communicationSkills.confidence}
                  </p>
                </div>
              )}
            </div>
          </div>
          <p className="text-gray-600 text-sm">{assessment.communicationSkills.analysis}</p>
          <ExpandableEvidence
            title="Observations"
            evidence={assessment.communicationSkills.evidence}
            icon={MessageCircle}
          />
        </div>
      )}

      {/* Technical Competency */}
      {assessment.technicalCompetency && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2 text-indigo-600" />
            Technical Competency
          </h3>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-gray-700">Overall Technical Score</h4>
              <span className="text-indigo-600 font-semibold">
                {assessment.technicalCompetency.score}/100
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {assessment.technicalCompetency.domainKnowledge !== undefined && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">Domain Knowledge</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {assessment.technicalCompetency.domainKnowledge}
                  </p>
                </div>
              )}
              {assessment.technicalCompetency.problemSolving !== undefined && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">Problem Solving</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {assessment.technicalCompetency.problemSolving}
                  </p>
                </div>
              )}
            </div>
          </div>
          <p className="text-gray-600 text-sm">{assessment.technicalCompetency.analysis}</p>
          <ExpandableEvidence
            title="Evidence"
            evidence={assessment.technicalCompetency.evidence}
            icon={Zap}
          />
        </div>
      )}

      {/* Strengths and Improvements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Strengths */}
        {assessment.strengths?.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
              Key Strengths
            </h3>
            <ul className="space-y-2">
              {assessment.strengths.map((strength, idx) => (
                <li key={idx} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{strength}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Improvements */}
        {assessment.improvements?.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-yellow-600" />
              Areas for Improvement
            </h3>
            <ul className="space-y-2">
              {assessment.improvements.map((improvement, idx) => (
                <li key={idx} className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{improvement}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Red Flags */}
      {assessment.redFlags?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
            <XCircle className="w-5 h-5 mr-2" />
            Red Flags
          </h3>
          <ul className="space-y-2">
            {assessment.redFlags.map((flag, idx) => (
              <li key={idx} className="flex items-start">
                <XCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-red-700">{flag}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Detailed Analysis */}
      {assessment.detailedAnalysis && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Brain className="w-5 h-5 mr-2 text-indigo-600" />
            Detailed Analysis
          </h3>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {assessment.detailedAnalysis}
          </p>
        </div>
      )}

      {/* Metadata */}
      {analysis?.metadata && (
        <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
          <p>
            <span className="font-semibold">Analysis Date:</span>{' '}
            {new Date(analysis.metadata.generatedAt || analysis.createdAt).toLocaleString()}
          </p>
          <p>
            <span className="font-semibold">Model:</span> {analysis.metadata.model || 'GPT-4o'}
          </p>
          {analysis.metadata.processingTime && (
            <p>
              <span className="font-semibold">Processing Time:</span>{' '}
              {analysis.metadata.processingTime.toFixed(2)}s
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TranscriptAnalysis;
